import apiClient from './apiClient';
import config from '../utils/config';
import { v4 as uuidv4 } from 'uuid';

/**
 * Service for chatbot-related API calls
 */
const chatbotService = {
  /**
   * Initialize a chat session
   * @param {string} projectId - Project ID
   * @param {Object} userData - Optional user data to include with the session
   * @param {string} jwt - Optional JWT token for authentication
   * @returns {Promise} Promise resolving to the new session data
   */
  initChatSession: async (projectId, userData = {}, jwt) => {
    try {
      console.log('Initializing chat session for project:', projectId, 'with user data:', userData);
      console.log('JWT token provided:', jwt ? 'YES' : 'NO');
      
      // Construct the full URL for the init endpoint
      const initEndpoint = `${config.API_BASE_URL}${config.CHATBOT_ENDPOINTS.INIT_SESSION}`;
      console.log('Endpoint URL:', initEndpoint);
      
      // Try to initialize session with the backend first
      if (projectId) {
        try {
          console.log('Attempting to connect to backend API at:', initEndpoint);
          const response = await apiClient.post(initEndpoint, {
            projectId,
            ...userData,
            jwt // Include JWT in the request body
          });
          console.log('Backend API response:', response);
          
          // Check if we have a valid session response
          if (response && response.status === 'success' && response.sessionId) {
            console.log('Successfully connected to backend. Session ID:', response.sessionId);
            // The backend now returns a clean 'messages' array with the greeting
            return {
              sessionId: response.sessionId,
              messages: response.messages || [],
              userData,
              fromBackend: true
            };
          } else {
            console.warn('Invalid session response from backend:', response);
            // Fallback to local session if response is invalid
          }
        } catch (error) {
          console.warn('Could not initialize session with backend, using local session:', error);
        }
      }
      
      console.log('Initializing local simulation session');
      // Fallback to local session if backend fails
      const sessionId = uuidv4();
      
      // Initial welcome message
      const initialMessages = [
        {
          role: 'assistant',
          content: 'Hello! How can I help you today?',
          timestamp: new Date()
        }
      ];
      
      // Return session data
      return {
        sessionId,
        messages: initialMessages,
        userData,
        fromBackend: false
      };
    } catch (error) {
      console.error('Error initializing chat session:', error);
      throw error;
    }
  },

  /**
   * Process a user message
   * @param {Object} messageData - Message data { message, sessionId, projectId, metadata, jwt }
   * @returns {Promise} Promise resolving to the chatbot response
   */
  processMessage: async (messageData) => {
    try {
      console.log('Processing message with data:', messageData);
      
      // Return a promise that will be resolved when the final message is received
      return new Promise((resolve, reject) => {
        // Create the full URL for the SSE endpoint using the API_BASE_URL from config
        const eventUrl = `${config.API_BASE_URL}${config.CHATBOT_ENDPOINTS.MESSAGE}`;
        
        // Add the message data for POST body
        const requestData = {
          message: messageData.message,
          sessionId: messageData.sessionId,
          projectId: messageData.projectId,
          userData: messageData.userData,
          jwt: messageData.jwt,
          // Add city and country from user metadata if available
          city: messageData.userData?.metadata?.city || null,
          country: messageData.userData?.metadata?.country || null
        };
        
        console.log('Connecting to SSE endpoint:', eventUrl);
        
        // Create a function to handle POST request with EventSource connection
        const sendPostRequestWithSSE = async () => {
          try {
            // Make an initial POST request to start the SSE connection
            const response = await fetch(eventUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                // Add authentication headers if needed
                ...(localStorage.getItem('token') ? { 'Authorization': `Bearer ${localStorage.getItem('token')}` } : {})
              },
              body: JSON.stringify(requestData)
            });
            
            // Check if the response is an event stream
            const contentType = response.headers.get('Content-Type');
            if (contentType && contentType.includes('text/event-stream')) {
              console.log('Received event stream response');
              
              // Create a reader to process the stream
              const reader = response.body.getReader();
              const decoder = new TextDecoder();
              
              // Variables to track the message status
              let intermediateMessage = null;
              let finalMessage = null;
              let buffer = '';
              
              // Function to process the stream
              const processStream = async () => {
                try {
                  // Read data from the stream
                  const { done, value } = await reader.read();
                  
                  // If the stream is closed, resolve with the final message
                  if (done) {
                    console.log('Stream closed');
                    if (finalMessage) {
                      resolve({ message: finalMessage });
                    } else if (intermediateMessage) {
                      // If we only have an intermediate message, use that as the final message
                      resolve({ message: intermediateMessage });
                    } else {
                      reject(new Error('Stream closed without a valid message'));
                    }
                    return;
                  }
                  
                  // Decode the data and add it to the buffer
                  buffer += decoder.decode(value, { stream: true });
                  
                  // Split the buffer into events
                  const events = buffer.split('\n\n');
                  buffer = events.pop() || ''; // Keep the last partial event in the buffer
                  
                  // Process each complete event
                  for (const event of events) {
                    // Parse the event lines
                    const lines = event.split('\n');
                    let eventType = '';
                    let eventData = '';
                    
                    for (const line of lines) {
                      if (line.startsWith('event:')) {
                        eventType = line.slice(6).trim();
                      } else if (line.startsWith('data:')) {
                        eventData = line.slice(5).trim();
                      }
                    }
                    
                    // Skip empty events
                    if (!eventType || !eventData) continue;
                    
                    console.log('Received event:', eventType, 'with data:', eventData);
                    
                    try {
                      const parsedData = JSON.parse(eventData);
                      
                      // Handle different event types
                      if (eventType === 'message') {
                        // This is the final message
                        finalMessage = parsedData.message;
                        console.log('Received final message:', finalMessage);
                        
                        // If we're done processing, resolve the promise
                        if (done) {
                          resolve({ message: finalMessage });
                          return;
                        }
                      } else if (eventType === 'intermediate') {
                        // This is an intermediate "I'll help you with that" message
                        intermediateMessage = parsedData.message;
                        console.log('Received intermediate message:', intermediateMessage);
                        
                        // Emit an event for the intermediate message
                        const intermediateEvent = new CustomEvent('neochat:intermediate', { 
                          detail: { message: intermediateMessage } 
                        });
                        window.dispatchEvent(intermediateEvent);
                      } else if (eventType === 'typing') {
                        // This is a typing indicator
                        console.log('Typing indicator received');
                        
                        // Emit an event for the typing indicator
                        const typingEvent = new CustomEvent('neochat:typing', { 
                          detail: { status: 'typing' } 
                        });
                        window.dispatchEvent(typingEvent);
                      } else if (eventType === 'error') {
                        // This is an error message
                        console.error('Error from server:', parsedData);
                        reject(new Error(parsedData.message || 'Unknown error'));
                        return;
                      }
                    } catch (error) {
                      console.error('Error parsing event data:', error);
                    }
                  }
                  
                  // Continue reading the stream
                  processStream();
                } catch (error) {
                  console.error('Error reading stream:', error);
                  reject(error);
                }
              };
              
              // Start processing the stream
              processStream();
            } else {
              // If the response is not an event stream, handle as regular JSON
              console.log('Received regular response');
              const data = await response.json();
              
              if (data?.data?.message) {
                resolve({ message: data.data.message });
              } else if (data?.message && typeof data.message === 'object') {
                resolve({ message: data.message });
              } else if (data?.message && typeof data.message === 'string') {
                resolve({ 
                  message: {
                    role: 'assistant',
                    content: data.message,
                    timestamp: new Date()
                  } 
                });
              } else {
                resolve(data);
              }
            }
          } catch (error) {
            console.error('Error in SSE fetch:', error);
            
            // If we get a syntax error in the pattern matching, it might be a CORS or URL issue
            if (error instanceof SyntaxError) {
              console.error('This appears to be a URL or CORS issue. Check the endpoint URL and CORS configuration.');
              console.error('Endpoint URL being used:', eventUrl);
              
              // Fallback to regular AJAX request using axios
              try {
                console.log('Falling back to regular axios request');
                const ajaxResponse = await apiClient.post(config.CHATBOT_ENDPOINTS.MESSAGE, requestData);
                
                if (ajaxResponse?.data?.message) {
                  resolve({ message: ajaxResponse.data.message });
                } else if (ajaxResponse?.message) {
                  resolve({ message: ajaxResponse.message });
                } else {
                  resolve(ajaxResponse);
                }
              } catch (ajaxError) {
                console.error('Fallback request also failed:', ajaxError);
                reject(ajaxError);
              }
            } else {
              reject(error);
            }
          }
        };
        
        // Start the SSE connection
        sendPostRequestWithSSE();
      });
    } catch (error) {
      console.error('Error processing message:', error);
      
      // Handle rate limit errors specifically
      if (error.response?.status === 429 || error.isRateLimitError) {
        // Extract the custom rate limit message
        const rateLimitMessage = error.rateLimitMessage || 
                               error.response?.data?.message || 
                               'Message rate limit exceeded. Please wait before sending more messages.';
        
        // Throw a special error with the rate limit info
        const rateLimitError = new Error(rateLimitMessage);
        rateLimitError.isRateLimitError = true;
        rateLimitError.rateLimitMessage = rateLimitMessage;
        throw rateLimitError;
      }
      
      // Pass through other errors
      throw error;
    }
  },

  /**
   * End a chat session
   * @param {Object} sessionData - Session data { sessionId, projectId }
   * @returns {Promise} Promise resolving to the end session response
   */
  endSession: async (sessionData) => {
    try {
      const endSessionEndpoint = `${config.API_BASE_URL}${config.CHATBOT_ENDPOINTS.END_SESSION}`;
      console.log('Ending session with endpoint:', endSessionEndpoint);
      const response = await apiClient.post(endSessionEndpoint, sessionData);
      return response;
    } catch (error) {
      console.error('Error ending session:', error);
      // Don't throw error here, just log it
      return { status: 'error', message: error.message };
    }
  },

  /**
   * Get cost breakdown for a project
   * @param {Object} params - Query parameters { projectId, year, month }
   * @returns {Promise} Promise resolving to the cost data
   */
  getCostBreakdown: async (params) => {
    try {
      const costEndpoint = `${config.API_BASE_URL}${config.CHATBOT_ENDPOINTS.COST}`;
      const response = await apiClient.get(costEndpoint, { params });
      return response;
    } catch (error) {
      console.error('Error getting cost breakdown:', error);
      throw error;
    }
  },

  /**
   * Get session count for a project
   * @param {Object} params - Query parameters { projectId, year, month }
   * @returns {Promise} Promise resolving to the session count data
   */
  getSessionCount: async (params) => {
    try {
      const sessionsEndpoint = `${config.API_BASE_URL}${config.CHATBOT_ENDPOINTS.SESSIONS}`;
      const response = await apiClient.get(sessionsEndpoint, { params });
      return response;
    } catch (error) {
      console.error('Error getting session count:', error);
      throw error;
    }
  },

  /**
   * Get chat history for a session
   * @param {string} sessionId - Session ID
   * @param {string} projectId - Project ID
   * @returns {Promise} Promise resolving to the chat history
   */
  getChatHistory: async (sessionId, projectId) => {
    try {
      const historyEndpoint = `${config.API_BASE_URL}${config.CHATBOT_ENDPOINTS.HISTORY(sessionId)}`;
      const response = await apiClient.get(historyEndpoint, {
        params: { projectId },
      });
      return response;
    } catch (error) {
      console.error('Error getting chat history:', error);
      throw error;
    }
  },
  
  /**
   * Generate monthly billing report
   * @param {string|null} projectId - Project ID (null for aggregate stats across all projects)
   * @param {number} year - Year
   * @param {number} month - Month (1-12)
   * @returns {Promise} Promise resolving to the billing report data
   */
  generateBillingReport: async (projectId, year, month) => {
    try {
      // If projectId is null, we're getting aggregate stats for all projects
      const isAggregate = projectId === null;
      const endpoint = isAggregate 
        ? `${config.API_BASE_URL}${config.CHATBOT_ENDPOINTS.AGGREGATE_SESSIONS}`
        : `${config.API_BASE_URL}${config.CHATBOT_ENDPOINTS.MONTHLY_SESSIONS}`;
      
      const params = isAggregate 
        ? { year, month } 
        : { projectId, year, month };
      
      const sessionData = await apiClient.get(endpoint, { params });
        
      console.log('Session Data: ', sessionData);

      // Calculate cost breakdown
      const costBreakdown = {
        primaryLLM: sessionData.data.totalPrimaryLLM || 0,
        secondaryLLM: sessionData.data.totalSecondaryLLM || 0,
        apiCost: sessionData.data.totalAPICost || 0,
        knowledgeBaseCost: sessionData.data.totalKnowledgeBaseCost || 0,
        totalCost: sessionData.data.totalCost || 0
      };

      // Calculate derived metrics
      const sessionCount = sessionData.data.sessionCount || 0;
      const totalMessages = sessionData.data.totalMessages || 0;
      const messagePerSession = totalMessages > 0 ? totalMessages / sessionCount : 0;
      const costPerSession = sessionCount > 0 ? costBreakdown.totalCost / sessionCount : 0;

      return {
        projectId,
        year,
        month,
        // Cost breakdown
        ...costBreakdown,
        // Usage statistics
        sessionCount,
        totalTokens: sessionData.data.totalTokens || 0,
        totalApiCalls: sessionData.data.totalApiCalls || 0,
        totalMessages,
        messagePerSession,
        costPerSession,
        // Additional metadata
        generatedAt: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Error generating billing report:', error);
      // Return empty data with zeros if there's an error
      return {
        projectId,
        year,
        month,
        // Cost breakdown
        primaryLLM: 0,
        secondaryLLM: 0,
        apiCost: 0,
        knowledgeBaseCost: 0,
        totalCost: 0,
        // Usage statistics
        sessionCount: 0,
        totalTokens: 0,
        totalApiCalls: 0,
        totalMessages: 0,
        messagePerSession: 0,
        costPerSession: 0,
        // Additional metadata
        generatedAt: new Date().toISOString(),
      };
    }
  },

  // Get session debug information
  getSessionDebugInfo: async (projectId, sessionId) => {
    try {
      console.log('[Debug API] Making request to:', `/projects/${projectId}/chatbot/admin/sessions/${sessionId}/debug`);
      const response = await apiClient.get(`/projects/${projectId}/chatbot/admin/sessions/${sessionId}/debug`);
      console.log('[Debug API] Full response:', response);
      console.log('[Debug API] Response status:', response.status);
      console.log('[Debug API] Response data field:', response.data);
      
      // Since apiClient automatically unwraps response.data, we get the backend response directly
      // Backend returns: {status: 'success', data: {...}}
      // ApiClient gives us: {status: 'success', data: {...}}
      if (response && response.status === 'success' && response.data) {
        console.log('[Debug API] Returning debug data:', response.data);
        return response.data;
      } else {
        console.warn('[Debug API] Unexpected response structure:', response);
        return null;
      }
    } catch (error) {
      console.error('[Debug API] Error fetching debug info:', error);
      throw error;
    }
  },

  // Get all chat sessions for a project
  getAllChatSessions: async (projectId, limit = null) => {
    try {
      const params = { projectId };
      if (limit) params.limit = limit;
      
      const response = await apiClient.get(`/projects/${projectId}/chat-sessions`, { params });
      return response.data;
    } catch (error) {
      console.error('Error getting chat sessions:', error);
      throw error;
    }
  },
};

export default chatbotService; 